import { SfLog } from '@app/adapters/logger'
import SFetch from '@app/utils/sfetch'

export default class ReportTable {
  constructor(container) {
    this.container = container
    this.dataLinePath = container.dataset.reportDataLinePath
    this.dataLineSelector = "[data-behaviour~='report-table-data-line']"
    this.dataLineOriginalValues = {}
    this.fetchDiscussionIndicators(
      container.dataset.reportTemplateId,
      container
    )
    this.eventListenerRemovers = []

    this.handleDataLineFocus = this.handleDataLineFocus.bind(this)
    this.handleDataLineChange = this.handleDataLineChange.bind(this)
    this.handleDataLineKeyUp = this.handleDataLineKeyUp.bind(this)
    this.handleDataLineSubmit = this.handleDataLineSubmit.bind(this)

    this.registerListeners()
  }

  async fetchDiscussionIndicators(reportTemplateId, container) {
    const url = Silverfin.Routes.discussion_indicators_report_path(
      Silverfin.currentFirmId,
      Silverfin.currentCompanyId,
      reportTemplateId
    )

    try {
      const data = await SFetch.json(url)

      const thead = container.querySelector('thead')
      const tbody = container.querySelector('tbody')

      const ledgerIndexes = {}
      let index = 0

      const headCells = Array.from(thead?.rows?.[0]?.cells) || []

      headCells.forEach((cell) => {
        const colspan = cell.colSpan || 1
        for (
          let n = 1, end = colspan, asc = 1 <= end;
          asc ? n <= end : n >= end;
          asc ? n++ : n--
        ) {
          const ledgerId = cell.dataset.ledgerId

          if (ledgerId) {
            if (!ledgerIndexes[ledgerId]) {
              ledgerIndexes[ledgerId] = []
            }

            ledgerIndexes[ledgerId].push(index)
          }
          index += 1
        }
      })

      const result = []

      for (const ledgerId in data) {
        const accountIds = data[ledgerId]
        const indexes = ledgerIndexes[ledgerId]

        if (indexes) {
          const result1 = []

          for (const row of Array.from(tbody.rows)) {
            const needle = row.dataset.accountId

            if (needle && accountIds.includes(parseInt(needle, 10))) {
              const result2 = []

              for (index of Array.from(indexes)) {
                result2.push(this.addDiscussionIndicator(row.cells[index]))
              }

              result1.push(result2)
            } else {
              result1.push(undefined)
            }
          }

          result.push(result1)
        }
      }

      return result
    } catch (e) {
      SfLog.warn('Failed to fetch discussion indicators', {
        extra: {
          error: e,
        },
      })
    }
  }

  addDiscussionIndicator(cell) {
    const discussionLink = cell.querySelector('a')

    if (discussionLink) {
      const replacementSpan = document.createElement('span')
      replacementSpan.classList.add('sf-c-primary')
      replacementSpan.innerText = discussionLink.innerText

      const icon = document.createElement('i')
      icon.classList.add('report-discussion-icon', 'sf-c-snow-darker')

      requestAnimationFrame(() => {
        cell.classList.add('cell-with-discussion-icon')

        discussionLink.classList.add('sf-level', 'sf-level-between', 'sf-full')
        discussionLink.dataset.testid = 'report-discussion-link'
        discussionLink.innerText = ''

        discussionLink.insertBefore(replacementSpan, discussionLink.firstChild)
        discussionLink.insertBefore(icon, discussionLink.firstChild)
      })
    }
  }

  handleDataLineChange(event) {
    const input = event.target
    const id = input.dataset.dataLineId

    if (this.dataLineOriginalValues[id] !== event.target.value) {
      input.dataset.modified = true

      requestAnimationFrame(() => {
        input.classList.add('report-table-data-line-modified')
      })
    }
  }

  handleDataLineFocus(event) {
    const id = event.target.dataset.dataLineId

    if (!this.dataLineOriginalValues[id]) {
      this.dataLineOriginalValues[id] = event.target.value
    }
  }

  handleDataLineKeyUp(event) {
    if (event.key === 'Enter' || event.keyCode === 13 || event.which === 13) {
      this.handleDataLineSubmit()
    } else {
      this.handleDataLineChange(event)
    }
  }

  handleDataLineSubmit() {
    const dataToPost = {}

    const dataLines = this.container.querySelectorAll(
      `${this.dataLineSelector}[data-modified]`
    )

    dataLines.forEach((dataline) => {
      const dataLineId = dataline.dataset.dataLineId
      const dataLineDate = dataline.dataset.dataLineEndDate
      const dataLineValueType = dataline.dataset.dataLineValueType

      if (!dataToPost[dataLineId]) {
        dataToPost[dataLineId] = {}
      }

      if (!dataToPost[dataLineId][dataLineDate]) {
        dataToPost[dataLineId][dataLineDate] = {}
      }

      dataToPost[dataLineId][dataLineDate][dataLineValueType] = dataline.value
    })

    Silverfin.Spinner.start()

    $.post(this.dataLinePath, {
      report_data_line_entries: dataToPost,
    }).fail(Silverfin.Spinner.failed())
  }

  registerListeners() {
    const dataLines = this.container.querySelectorAll(this.dataLineSelector)

    dataLines.forEach((dataline) => {
      dataline.addEventListener('focus', this.handleDataLineFocus)
      dataline.addEventListener('keyup', this.handleDataLineKeyUp)

      this.eventListenerRemovers.push(() => {
        dataline.removeEventListener('focus', this.handleDataLineFocus)
        dataline.removeEventListener('keyup', this.handleDataLineKeyUp)
      })
    })
  }

  teardown() {
    this.container = undefined
    this.eventListenerRemovers.forEach((remover) => remover())
  }
}
